import { heatmapSelector } from '_store/heatmap/selectors';
import {
  DAY_GRANULARITIES,
  daySeries,
  dayStats,
  getDataset,
  getVehicleCounts,
  heatmapKey,
  MS_DAY,
  MS_HOUR,
  MS_QUARTER_HOUR,
  round2,
  scatterPoints,
  splitDays,
  vehicleDaySeries,
  vehicleKey,
} from '_store/heatmap/utils';
import { useLocalization } from '@fluent/react';
import { IconButton, Typography } from '@material-ui/core';
import { fade, makeStyles, useTheme } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ScatterController,
  Tooltip,
} from 'chart.js';
import { format } from 'date-fns';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  ScatterController,
  CategoryScale,
  LinearScale,
  Tooltip,
);

const useStyles = makeStyles((theme) => ({
  row: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
  chart: { position: 'relative', width: '100%', height: 260 },
  label: { color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5) },
}));

const GRANULARITY_KEY: Record<number, string> = {
  [MS_HOUR]: '1h',
  [MS_HOUR / 2]: '30m',
  [MS_QUARTER_HOUR]: '15m',
};

const DayComparison: FC<{ followSlot?: [number, number] | null }> = ({ followSlot = null }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { l10n } = useLocalization();
  const { active, baseRange, dataKey, status, vehicleCounts } = useSelector(heatmapSelector);
  const [start, end] = baseRange;
  const metric = active === 'speed_value' ? 'speed_value' : 'counter';
  const ds = dataKey && dataKey === heatmapKey(metric, start, end) ? getDataset(dataKey) : null;
  // Counter: unique vehicles from the over-time endpoint, not the summed grid cells
  const vc = vehicleCounts.key === vehicleKey(start, end) ? getVehicleCounts(vehicleCounts.key) : null;

  const dataStatus = metric === 'counter' ? vehicleCounts.status : status;
  const [granularity, setGranularity] = useState(MS_HOUR);
  const [day, setDay] = useState(0);
  // Back to day 1 whenever the interval or metric changes
  useEffect(() => setDay(0), [start, end, metric]);

  const days = useMemo(() => (start < end ? splitDays(start, end) : []), [start, end]);
  const series = useMemo(
    () => {
      if (metric === 'counter') return vc ? days.map((d) => vehicleDaySeries(vc, d, granularity)) : [];
      return ds ? days.map((d) => daySeries(ds, d, granularity)) : [];
    },
    [ds, vc, metric, days, granularity],
  );
  // Average of every day of the period per time slot, whichever day is shown
  const average = useMemo(() => dayStats(series).mean, [series]);
  // The first chart's selected slot picks the day here (and its bars are highlighted below)
  const slotStart = followSlot?.[0] ?? null;
  useEffect(() => {
    if (slotStart === null) return;
    const i = days.findIndex((d, k) => slotStart < (days[k + 1]?.dayStart ?? Infinity));
    if (i >= 0) setDay(i);
  }, [slotStart, days]);
  const current = series[day] ?? [];
  // Bars of the shown day covered by the slot; a whole-day slot highlights nothing
  const dayStart = days[day]?.dayStart ?? 0;
  const hlFrom = followSlot ? Math.floor((followSlot[0] - dayStart) / granularity) : 0;
  const hlTo = followSlot ? Math.ceil((followSlot[1] - dayStart) / granularity) : 0;
  const highlighted = followSlot !== null && followSlot[1] - followSlot[0] < MS_DAY;
  // Fixed y axis across days (max of any day, whichever is shown) so stepping doesn't rescale
  const yMax = useMemo(
    () => series.reduce((m, s) => s.reduce<number>((a, v) => (v !== null && v > a ? v : a), m), 0),
    [series],
  );

  const t = (id: string, args?: Record<string, string | number>) => l10n.getString(id, args);
  const labels = useMemo(
    () => current.map((_, b) => format(new Date(2000, 0, 1, 0, 0, 0, b * granularity), 'HH:mm')),
    [current, granularity],
  );

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: days[day] ? format(new Date(days[day].dayStart), 'EEE d MMM') : '',
          data: current,
          backgroundColor: current.map((_, b) =>
            fade(theme.palette.primary.main, highlighted && b >= hlFrom && b < hlTo ? 1 : 0.6),
          ),
          order: 2,
        },
        {
          type: 'line' as const,
          label: t('heatmap-days-average'),
          data: average,
          borderColor: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.main,
          borderWidth: 3,
          pointRadius: 0,
          spanGaps: false,
          order: 1,
        },
      ],
    }),
    [labels, current, average, theme, days, day, l10n, highlighted, hlFrom, hlTo],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (c: { dataset: { label?: string }; parsed: { y: number } }) =>
              `${c.dataset.label}: ${round2(c.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: theme.palette.text.secondary, maxRotation: 0, autoSkipPadding: 12 },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          max: yMax > 0 ? yMax : undefined,
          ticks: { color: theme.palette.text.secondary, callback: (v: string | number) => round2(Number(v)) },
          grid: { color: theme.palette.divider },
          title: {
            display: true,
            color: theme.palette.text.secondary,
            text: t(metric === 'speed_value' ? 'heatmap-chart-y-speed' : 'heatmap-chart-y-counter'),
          },
        },
      },
    }),
    [theme, l10n, metric, yMax],
  );

  // Speed: scatter of the shown day's cell-quarter averages (x = time of day) with the day's and the
  // whole period's average per time slot as lines; points inside the first chart's slot stand out.
  const isSpeed = metric === 'speed_value';
  const speedYMax = useMemo(() => (ds && isSpeed ? ds.v.reduce((m, v) => (v > m ? v : m), 0) : 0), [ds, isSpeed]);
  const scatter = useMemo(() => {
    const d = days[day];
    if (!ds || !isSpeed || !d) return { inSlot: [], rest: [], total: 0 };
    const { points, total } = scatterPoints(ds, d.start, d.end);
    const inSlot: Array<{ x: number; y: number }> = [];
    const rest: Array<{ x: number; y: number }> = [];
    points.forEach((p) => {
      const hit = highlighted && followSlot && p.x >= followSlot[0] && p.x < followSlot[1];
      (hit ? inSlot : rest).push({ x: p.x - d.dayStart, y: p.y });
    });
    return { inSlot, rest, total };
  }, [ds, isSpeed, days, day, highlighted, followSlot]);
  const toLine = (vals: Array<number | null>) =>
    vals.map((y, b) => ({ x: (b + 0.5) * granularity, y: y as number }));
  const scatterData = useMemo(
    () => ({
      datasets: [
        {
          type: 'scatter' as const,
          label: t('heatmap-scatter-detections'),
          data: scatter.rest,
          pointRadius: 2,
          backgroundColor: fade(theme.palette.primary.main, highlighted ? 0.2 : 0.35),
          order: 3,
        },
        {
          type: 'scatter' as const,
          label: t('heatmap-scatter-selected'),
          data: scatter.inSlot,
          pointRadius: 3,
          backgroundColor: theme.palette.primary.main,
          order: 2,
        },
        {
          type: 'line' as const,
          label: t('heatmap-scatter-day-average'),
          data: toLine(current),
          borderColor: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.main,
          borderWidth: 3,
          pointRadius: 0,
          spanGaps: false,
          order: 1,
        },
        {
          type: 'line' as const,
          label: t('heatmap-scatter-period-average'),
          data: toLine(average),
          borderColor: theme.palette.text.primary,
          backgroundColor: theme.palette.text.primary,
          borderWidth: 2,
          borderDash: [8, 5],
          pointRadius: 0,
          spanGaps: false,
          order: 0,
        },
      ],
    }),
    [scatter, current, average, granularity, theme, highlighted, l10n],
  );
  const clock = (ms: number) => format(new Date(2000, 0, 1, 0, 0, 0, ms), 'HH:mm');
  const scatterOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: {
        // the "selected slot" dataset is the same series as the dots, so it gets no legend entry
        legend: {
          display: true,
          labels: {
            color: theme.palette.text.secondary,
            boxWidth: 12,
            filter: (item: { datasetIndex?: number }) => item.datasetIndex !== 1,
          },
        },
        tooltip: {
          callbacks: {
            label: (c: { dataset: { label?: string }; parsed: { x: number; y: number } }) =>
              `${c.dataset.label} · ${clock(c.parsed.x)} · ${round2(c.parsed.y)} km/h`,
          },
        },
      },
      scales: {
        x: {
          type: 'linear' as const,
          min: 0,
          max: MS_DAY,
          ticks: {
            color: theme.palette.text.secondary,
            stepSize: 3 * MS_HOUR,
            callback: (v: string | number) => clock(Number(v)),
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          max: speedYMax > 0 ? speedYMax : undefined,
          ticks: { color: theme.palette.text.secondary, callback: (v: string | number) => round2(Number(v)) },
          grid: { color: theme.palette.divider },
          title: { display: true, color: theme.palette.text.secondary, text: t('heatmap-chart-y-speed') },
        },
      },
    }),
    [theme, l10n, speedYMax],
  );

  const pad = (n: number) => String(n).padStart(String(days.length).length, ' ');

  if (active === 'inactive' || days.length === 0) return null;

  return (
    <div>
      <Typography variant="body2" className={classes.label}>
        {t(isSpeed ? 'heatmap-scatter-title' : 'heatmap-days-title')}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {t(isSpeed ? 'heatmap-scatter-hint' : 'heatmap-days-hint')}
        {isSpeed &&
          scatter.total > scatter.rest.length + scatter.inSlot.length &&
          ` ${t('heatmap-scatter-sampled', {
            shown: scatter.rest.length + scatter.inSlot.length,
            total: scatter.total,
          })}`}
      </Typography>
      <div className={classes.row} style={{ marginTop: theme.spacing(1) }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={granularity}
          onChange={(_, v) => v && setGranularity(v)}
        >
          {DAY_GRANULARITIES.map((g) => (
            <ToggleButton key={g} value={g}>
              {t(`heatmap-days-granularity-${GRANULARITY_KEY[g]}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <IconButton size="small" title={t('heatmap-days-previous')} disabled={day <= 0} onClick={() => setDay(day - 1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2" style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
          {days[day] ? format(new Date(days[day].dayStart), 'EEE dd MMM') : ''}
          {` · ${t('heatmap-days-position', { n: pad(day + 1), total: days.length })}`}
        </Typography>
        <IconButton
          size="small"
          title={t('heatmap-days-next')}
          disabled={day >= days.length - 1}
          onClick={() => setDay(day + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>
      {(metric === 'counter' ? vc : status === 'ready' && ds) ? (
        <div className={classes.chart} style={isSpeed ? { height: 420 } : undefined}>
          {isSpeed ? (
            <Chart type="scatter" data={scatterData} options={scatterOptions} />
          ) : (
            <Chart type="bar" data={data} options={options} />
          )}
        </div>
      ) : (
        <Typography variant="caption" color="textSecondary">
          {t(dataStatus === 'loading' ? 'heatmap-loading' : 'dashboard-heatmap-no-data')}
        </Typography>
      )}
    </div>
  );
};

export default React.memo(DayComparison);
